import {
  Button, IconComponent, NextArrow, Text,
} from '@myinterview/component-library';
import { useActor } from '@xstate/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DoneInvokeEvent, ActorRef } from 'xstate';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import i18n from 'i18next';
import { PreviewInterface } from '../interfaces/previewInterface';
import { IDimensions, PreviewItem } from './PreviewItem';
import { Retake } from './icons/Retake';
import { QuestionNumber } from './ui/QuestionNumber';
import { Errors } from './Errors';
import { isMobile } from '../utils/device.utils';

interface IProps {
  previewRef: ActorRef<DoneInvokeEvent<any>>
  onNextQuestion: (questionNumber: number) => void;
  onNextRetake: () => void;
  onQuestionMode: () => void;
  isRetakeAvailable: boolean;
  totalRetakes: number;
  isLastQuestion: boolean;
  isQuestionMode: boolean;
  isConnected: boolean;
  currentQuestion: number;
  numberOfQuestions:number;
  myinterviewRef: React.RefObject<HTMLDivElement>;
}

const displayedItemsNumber = isMobile ? 1 : 3;
const CHANGE_SLIDE_COOLDOWN = 30;
const SLIDE_RATIO = 0.25;
const SLIDE_EDGE_TRANSFORM_RATIO = 0.25;
const TRANSITION_DURATION = '300ms';

const TITLE_TEXT_OPTIONS = { // TODO: Translations
  LAST_QUESTION_SINGLE_TAKE: () => i18n.t('preview.question_title.last_question_single_take'),
  ONLY_ONE_TAKE: () => i18n.t('preview.question_title.only_one_take'),
  LAST_TAKE: () => i18n.t('preview.question_title.last_take'),
  AVAILABLE_TAKES_FIRST_ATTEMPT: () => i18n.t('preview.question_title.available_takes_first_attempt'),
  AVAILABLE_TAKES: () => i18n.t('preview.question_title.available_takes'),
};

const LAST_PRACTICE_TITLE_TEXT_OPTIONS: typeof TITLE_TEXT_OPTIONS = { // TODO: Translations
  LAST_QUESTION_SINGLE_TAKE: () => i18n.t('preview.practice_title.last_question_single_take'),
  ONLY_ONE_TAKE: () => i18n.t('preview.practice_title.only_one_take'),
  LAST_TAKE: () => i18n.t('preview.practice_title.last_take'),
  AVAILABLE_TAKES_FIRST_ATTEMPT: () => i18n.t('preview.practice_title.available_takes_first_attempt'),
  AVAILABLE_TAKES: () => i18n.t('preview.practice_title.available_takes'),
};

export const PreviewList:React.FC<IProps> = ({
  previewRef, onNextQuestion, onNextRetake, onQuestionMode, isRetakeAvailable, totalRetakes, isLastQuestion, isQuestionMode, isConnected, currentQuestion,
  numberOfQuestions, myinterviewRef,
}) => {
  const { t } = useTranslation();
  const [previewMachine] = useActor(previewRef);
  const { videosUrls } = previewMachine.context as PreviewInterface;
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(videosUrls.length === 1 ? 0 : null);
  const [previewVideoIndex, setPreviewVideoIndex] = useState(Math.max(videosUrls.length - displayedItemsNumber, 0));
  const [previewModeIndex, setPreviewModeIndex] = useState<number | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const itemsWrapperRef = useRef<HTMLDivElement | null>(null);
  const dynamicVideoRef = useRef<HTMLVideoElement | null>(null);
  const prevTouchX = useRef<number>(0);
  const transformX = useRef<number>(0);
  const previewIndex = useRef<number>(Math.max(videosUrls.length - displayedItemsNumber, 0));
  const lastMoveEventTime = useRef<number>(0);
  const isPredictionNext = useRef<boolean>(false);
  const isPreviewModal = isMobile && previewModeIndex !== null;

  useEffect(() => {
    setSelectedVideoIndex(videosUrls.length === 1 ? 0 : null);
  }, [videosUrls.length]);

  const titleText = useMemo(() => {
    const TITLE_TEXT: { [K in keyof typeof TITLE_TEXT_OPTIONS]: boolean } = {
      LAST_QUESTION_SINGLE_TAKE: videosUrls.length === 1 && isLastQuestion,
      ONLY_ONE_TAKE: videosUrls.length === 1 && !isRetakeAvailable,
      LAST_TAKE: !isRetakeAvailable,
      AVAILABLE_TAKES_FIRST_ATTEMPT: videosUrls.length === 1 && isRetakeAvailable,
      AVAILABLE_TAKES: isRetakeAvailable,
    };
    const selectedOptionKey = ((Object.entries(TITLE_TEXT).find(([_, val]) => val) || [])[0] || 'LAST_TAKE') as keyof typeof TITLE_TEXT_OPTIONS;
    const isLastPracticeQuestion = isLastQuestion && !isQuestionMode;
    return (isLastPracticeQuestion ? LAST_PRACTICE_TITLE_TEXT_OPTIONS : TITLE_TEXT_OPTIONS)[selectedOptionKey]();
  }, [isRetakeAvailable, videosUrls.length, isLastQuestion]);

  const updateWrapperPosition = () => {
    if (itemsWrapperRef.current) {
      itemsWrapperRef.current.style.transform = `translate3d(${transformX.current}px, 0px, 0px)`;
    }
  };

  const updateTransformX = (value: number) => {
    const width = itemsWrapperRef.current?.clientWidth || 0;
    const newVal = transformX.current + value;
    transformX.current += (newVal > 0 || -newVal > (width * (videosUrls.length - 1))) ? value * SLIDE_EDGE_TRANSFORM_RATIO : value;
    itemsWrapperRef.current?.style.setProperty('--myinterview-widget-preview-transform-x', `${width * previewIndex.current + newVal}px`);
    updateWrapperPosition();
    isPredictionNext.current = value < 0;
  };

  const adjustWrapperPosition = () => {
    if (itemsWrapperRef.current) {
      const width = itemsWrapperRef.current?.clientWidth || 0;
      const indexOffset = Math.max(previewIndex.current - displayedItemsNumber, previewIndex.current);
      transformX.current = (-width / displayedItemsNumber) * indexOffset;
      itemsWrapperRef.current.style.setProperty('--myinterview-widget-preview-transform-x', '0px');
      itemsWrapperRef.current.style.setProperty('--myinterview-widget-preview-item-transition', TRANSITION_DURATION);
      itemsWrapperRef.current.style.transitionDuration = TRANSITION_DURATION;
      updateWrapperPosition();
    }
  };

  const updatePreviewIndex = () => {
    const width = itemsWrapperRef.current?.clientWidth || 0;
    const shouldChangeSlide = (Date.now() - lastMoveEventTime.current < CHANGE_SLIDE_COOLDOWN)
      || Math.abs(width * (previewIndex.current + 1) + (transformX.current - width)) > width * SLIDE_RATIO;
    previewIndex.current = shouldChangeSlide
      ? Math.max(0, Math.min(previewIndex.current + (isPredictionNext.current ? 1 : -1), videosUrls.length - 1))
      : previewIndex.current;
    setPreviewVideoIndex(previewIndex.current);
    adjustWrapperPosition();
  };

  const onUpdatePreviewIndex = (value: number) => {
    const valToSet = Math.max(0, Math.min(value, videosUrls.length - 1));
    previewIndex.current = valToSet;
    setPreviewVideoIndex(valToSet);
    adjustWrapperPosition();
  };

  // TODO: check mouseborwsing
  // const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
  //   if (e.buttons === 1) {
  //     lastMoveEventTime.current = Date.now();
  //     if (e.movementX) {
  //       updateTransformX(e.movementX);
  //     }
  //   }
  // };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    lastMoveEventTime.current = Date.now();
    if (prevTouchX.current) {
      const diff = e.touches[0].pageX - prevTouchX.current;
      if (diff) {
        updateTransformX(diff);
      }
    }
    prevTouchX.current = e.touches[0].pageX;
  };

  // TODO: check mouseborwsing
  // const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
  //   if (e.type === 'mouseleave' && !e.buttons) return;
  //   updatePreviewIndex();
  // };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    prevTouchX.current = 0;
    updatePreviewIndex();
  };

  const resetTransition = () => {
    if (itemsWrapperRef.current) {
      itemsWrapperRef.current.style.setProperty('--myinterview-widget-preview-item-transition', '0ms');
      itemsWrapperRef.current.style.transitionDuration = '0ms';
    }
  };

  useEffect(() => {
    myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-height', '40px');
    myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-preview-display', 'block');
    if (itemsWrapperRef.current) {
      itemsWrapperRef.current.style.setProperty('--myinterview-widget-item-width', `calc(100% / ${displayedItemsNumber})`);
    }

    return () => {
      myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-height', 'calc(40vh - 100px)');
      myinterviewRef?.current?.style.setProperty('--myinterview-widget-background-preview-display', 'none');
    };
  }, []);

  const handleItemsWrapperRef = useCallback((node: HTMLDivElement) => {
    itemsWrapperRef.current = node;
    adjustWrapperPosition();
  }, []);

  useEffect(() => { // Fix for older react versions
    setPreviewVideoIndex(Math.max(videosUrls.length - displayedItemsNumber, 0));
    previewIndex.current = Math.max(videosUrls.length - displayedItemsNumber, 0);
    adjustWrapperPosition();
  }, [videosUrls.length]);

  useEffect(() => {
    setPreviewModeIndex(null);
  }, [previewVideoIndex]);

  const handleDynamicVideoRef = useCallback((node: HTMLVideoElement) => {
    dynamicVideoRef.current = node;
    dynamicVideoRef.current?.load();
  }, []);

  const onSetPreviewDimensions = (dimensions: IDimensions) => {
    const { x, y, width, height } = dimensions;
    if (dynamicVideoRef.current) {
      dynamicVideoRef.current.style.setProperty('--myinterview-preview-video-top', `${y}px`);
      dynamicVideoRef.current.style.setProperty('--myinterview-preview-video-left', `${x}px`);
      dynamicVideoRef.current.style.setProperty('--myinterview-preview-video-width', `${width}px`);
      dynamicVideoRef.current.style.setProperty('--myinterview-preview-video-height', `${height}px`);
    }
  };

  const onPreviewMode = (idx: number, dimensions: IDimensions) => {
    onSetPreviewDimensions(dimensions);
    setPreviewModeIndex(idx);
    dynamicVideoRef.current?.play();
  };

  const onStopVideo = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPreviewModeIndex(null);
    if (dynamicVideoRef.current) {
      dynamicVideoRef.current.pause();
      dynamicVideoRef.current.currentTime = 0;
    }
    myinterviewRef?.current?.style.setProperty('--myinterview-widget-dynamic-overflow', 'auto');
  };

  const dynamicVideoClassNames = classNames({
    'myinterview-widget-preview__dynamic-video': true,
    'myinterview-widget-preview__dynamic-video--preview': isPreviewModal,
  });

  const itemsWrapperClassNames = classNames({
    'myinterview-widget-preview__items-wrapper': true,
    'myinterview-widget-preview__items-wrapper--centered': videosUrls.length < displayedItemsNumber,
  });

  return (
    <div className="myinterview-widget-preview__container">
      <QuestionNumber currentQuestion={currentQuestion} numberOfQuestions={numberOfQuestions} />

      <Text className="myinterview-widget-preview__title" size="H2-Bold">
        {titleText}
      </Text>

      <div ref={mainRef} className="myinterview-widget-preview__main">

        {previewVideoIndex > 0 && (
          <div className="myinterview-widget-preview__arrow myinterview-widget-preview__arrow--prev">
            <div
              className="myinterview-widget-preview__arrow-icon-wrapper"
              onClick={() => onUpdatePreviewIndex(previewVideoIndex - 1)}
              role="presentation"
            >
              <NextArrow />
            </div>
          </div>
        )}

        <div
          role="presentation"
          ref={handleItemsWrapperRef}
          className={itemsWrapperClassNames}
          // onMouseDown={resetTransition}
          // onMouseMove={handleMouseMove}
          // onMouseUp={handleMouseUp}
          // onMouseLeave={handleMouseUp}
          onTouchStart={resetTransition}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTransitionEnd={resetTransition}
        >
          {videosUrls.map((videoUrl, idx) => (
            <PreviewItem
              myinterviewRef={myinterviewRef}
              key={videoUrl}
              blob={videoUrl}
              isSelected={selectedVideoIndex === idx}
              onSelectVideo={() => setSelectedVideoIndex(idx)}
              position={idx - previewVideoIndex}
              currentIndex={idx}
              numberOfVideos={videosUrls.length}
              isPreviewMode={previewModeIndex === idx}
              onPreviewMode={(dimensions) => onPreviewMode(idx, dimensions)}
            />
          ))}
        </div>

        {previewVideoIndex + displayedItemsNumber < videosUrls.length && (
          <div className="myinterview-widget-preview__arrow">
            <div
              className="myinterview-widget-preview__arrow-icon-wrapper"
              onClick={() => onUpdatePreviewIndex(previewVideoIndex + 1)}
              role="presentation"
            >
              <NextArrow />
            </div>
          </div>
        )}
      </div>

      {isMobile && videosUrls.length > 1 && (
        <>
          <div className="myinterview-widget-preview__dots-wrapper">
            {videosUrls.map((videoUrl, idx) => (
              <span
                key={videoUrl}
                className={`myinterview-widget-preview__dot ${idx === previewVideoIndex ? 'myinterview-widget-preview__dot--selected' : ''}`}
                onClick={() => onUpdatePreviewIndex(idx)}
                role="presentation"
              />
            ))}
          </div>
          <span className="myinterview-widget-preview__horizontal-line" />
        </>
      )}

      <div className="myinterview-widget-preview__preview-buttons">
        <div className="myinterview-widget-preview__preview-buttons-top">
          {/* TODO : Change button component to include icon */}
          {isRetakeAvailable && (
          <div className="myinterview-widget-preview__retakes-wrapper">
            <Button
              className="myinterview-widget-preview__preview-button myinterview-widget-preview__preview-button--retake"
              onClick={onNextRetake}
              disabled={!isConnected || !isRetakeAvailable}
              backgroundColor="white"
              color="neutral-90"
            >
              <>
                <div style={{ marginRight: '10px' }}>
                  <IconComponent size="16px" icon={<Retake />} />
                </div>
                {t('buttons.RETAKE')}
              </>
            </Button>
            {isQuestionMode && (
            <p className="myinterview-widget-preview__retakes-left myinterview-text--XS myinterview-text--regular myinterview-widget--rtl-support">
              {t(
                'recorder.explanation.remaining_take',
                { count: totalRetakes - videosUrls.length },
              )}
            </p>
            )}
          </div>
          )}

          {(!isLastQuestion || isQuestionMode) && (
          <Button
            className="myinterview-widget-preview__preview-button myinterview-widget-preview__preview-button--next"
            disabled={!isConnected || selectedVideoIndex === null}
            onClick={() => selectedVideoIndex !== null && onNextQuestion(selectedVideoIndex + 1)}
          >
            {t('buttons.SUBMIT')}
          </Button>
          )}
        </div>

        {!isQuestionMode && (
        <Button
          className="myinterview-widget-preview__preview-button myinterview-widget-preview__preview-button--start-interview"
          disabled={!isConnected}
          onClick={onQuestionMode}
          color="primary"
        >
          {t('buttons.READY')}
        </Button>
        )}
      </div>

      {isMobile && (
      <>
        <video
          ref={handleDynamicVideoRef}
          className={dynamicVideoClassNames}
          src={videosUrls[previewVideoIndex]}
          playsInline
          controls={isPreviewModal}
          onContextMenu={(e) => { e.preventDefault(); return false; }}
          disablePictureInPicture
          controlsList="nodownload"
        />

        <div
          className={classNames('myinterview-widget-preview__background', { 'myinterview-widget-preview__background--hidden': !isPreviewModal })}
          role="presentation"
          onTouchEnd={onStopVideo}
        >
          <div className="myinterview-widget-preview__close">
            <Text size="M-Semibold" color="white">Close</Text>
          </div>
        </div>
      </>
      )}

      {!isConnected && <Errors errorType="CONNECTION" />}

    </div>
  );
};
